import type {
  EventRow,
  MalaysiaHoliday,
} from "../../lib/webAppApi";

import "./ExpandableDateDetails.css";

type ExpandableDateDetailsProps = {
  selectedDate: Date;
  holidays: MalaysiaHoliday[];
  events: EventRow[];
  loading: boolean;
  onAddEvent: () => void;
  onDeleteEvent: (
    event: EventRow,
  ) => void;
};

function dateTitle(date: Date) {
  return new Intl.DateTimeFormat(
    "en-MY",
    {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    },
  ).format(date);
}

function eventTime(event: EventRow) {
  if (event.allDay) {
    return "All day";
  }

  return new Intl.DateTimeFormat(
    "en-MY",
    {
      hour: "numeric",
      minute: "2-digit",
    },
  ).format(new Date(event.startAt));
}

function eventIcon(iconKey: string) {
  const value =
    iconKey.trim().toLowerCase();

  if (
    !value ||
    value === "calendar" ||
    value === "event"
  ) {
    return "▣";
  }

  if (
    value.includes("birthday") ||
    value.includes("cake")
  ) {
    return "◈";
  }

  if (
    value.includes("food") ||
    value.includes("dinner")
  ) {
    return "◉";
  }

  if (
    value.includes("travel") ||
    value.includes("flight")
  ) {
    return "✈";
  }

  if (
    value.includes("work") ||
    value.includes("meeting")
  ) {
    return "◆";
  }

  if (
    value.includes("health") ||
    value.includes("doctor")
  ) {
    return "✚";
  }

  return iconKey.length <= 3
    ? iconKey
    : "✦";
}

function mapUrl(event: EventRow) {
  if (
    Number.isFinite(event.latitude) &&
    Number.isFinite(event.longitude)
  ) {
    return (
      "https://www.google.com/maps/dir/" +
      `?api=1&destination=${event.latitude},` +
      event.longitude
    );
  }

  if (
    event.placeId &&
    event.location
  ) {
    return (
      "https://www.google.com/maps/search/" +
      `?api=1&query=${encodeURIComponent(
        event.location,
      )}&query_place_id=${encodeURIComponent(
        event.placeId,
      )}`
    );
  }

  return (
    "https://www.google.com/maps/search/" +
    `?api=1&query=${encodeURIComponent(
      event.location,
    )}`
  );
}

export function ExpandableDateDetails({
  selectedDate,
  holidays,
  events,
  loading,
  onAddEvent,
  onDeleteEvent,
}: ExpandableDateDetailsProps) {
  const total =
    holidays.length + events.length;

  return (
    <section
      className="calendar-expanded"
      aria-live="polite"
    >
      <header className="calendar-expanded__header">
        <div>
          <span>SELECTED DATE</span>

          <h2>{dateTitle(selectedDate)}</h2>

          <p>
            {loading
              ? "Loading details…"
              : total === 0
                ? "No events or public holidays."
                : `${total} item${
                    total === 1 ? "" : "s"
                  } on this date.`}
          </p>
        </div>

        <button
          className="button primary"
          type="button"
          onClick={onAddEvent}
        >
          Add event
        </button>
      </header>

      <div className="calendar-expanded__scroll">
        {loading && (
          <div className="calendar-expanded__empty">
            Loading calendar details…
          </div>
        )}

        {!loading && total === 0 && (
          <div className="calendar-expanded__empty">
            <strong>Your day is clear.</strong>

            <p>
              Add a personal event, routine reminder,
              or Event Circle plan for this date.
            </p>
          </div>
        )}

        {!loading && holidays.length > 0 && (
          <section className="calendar-expanded__group">
            <h3>
              <i className="calendar-expanded__dot calendar-expanded__dot--holiday" />
              Public holidays
            </h3>

            {holidays.map((holiday) => (
              <article
                className="calendar-detail-card calendar-detail-card--holiday"
                key={holiday.id}
              >
                <div>
                  <span>PUBLIC HOLIDAY</span>

                  <h4>{holiday.name}</h4>

                  <p>
                    {holiday.states.join(", ")}
                  </p>
                </div>

                {holiday.type && (
                  <small>{holiday.type}</small>
                )}
              </article>
            ))}
          </section>
        )}

        {!loading && events.length > 0 && (
          <section className="calendar-expanded__group">
            <h3>
              <i className="calendar-expanded__dot calendar-expanded__dot--event" />
              Your events
            </h3>

            {events.map((event) => (
              <article
                className="calendar-detail-card calendar-detail-card--event"
                key={event.$id}
              >
                <div className="calendar-detail-card__icon">
                  {eventIcon(event.iconKey)}
                </div>

                <div className="calendar-detail-card__content">
                  <span>
                    {event.category || "PERSONAL"} ·{" "}
                    {eventTime(event)}
                  </span>

                  <h4>{event.title}</h4>

                  {event.location && (
                    <a
                      className="calendar-detail-card__location"
                      href={mapUrl(event)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <b>⌖</b>

                      <div>
                        <strong>
                          {event.location}
                        </strong>

                        <small>
                          {event.latitude !==
                            undefined &&
                          event.longitude !==
                            undefined
                            ? "Exact location · Open directions"
                            : "Open in Google Maps"}
                        </small>
                      </div>

                      <i>↗</i>
                    </a>
                  )}

                  {event.notes && (
                    <p className="calendar-detail-card__notes">
                      {event.notes}
                    </p>
                  )}
                </div>

                <button
                  type="button"
                  className="calendar-detail-card__delete"
                  onClick={() => onDeleteEvent(event)}
                  aria-label={`Delete ${event.title}`}
                  title="Delete event"
                >
                  ×
                </button>
              </article>
            ))}
          </section>
        )}
      </div>
    </section>
  );
}